import React from 'react';
import { LogOut, Users, LayoutDashboard, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, Link } from 'react-router-dom';
import Logo from './Logo';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  const isActivePath = (path: string) => location.pathname === path;

  return (
    <div className="flex flex-col min-h-screen bg-dark-900">
      {!isLoginPage && currentUser && (
        <header className="border-b bg-dark-800 border-dark-700">
          <div className="px-4 py-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div className="flex items-center">
                <Link to="/dashboard" className="flex items-center space-x-2 transition-colors text-primary-500 hover:text-primary-400">
                  <Logo className="w-8 h-8" />
                  <span className="text-xl font-bold tracking-tight">WinMax Optimizer Licensing</span>
                </Link>
              </div>

              <nav className="flex items-center space-x-1">
                <Link
                  to="/dashboard"
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActivePath('/dashboard')
                    ? 'bg-dark-700 text-primary-500'
                    : 'text-dark-300 hover:text-dark-100 hover:bg-dark-700'
                    } transition-colors`}
                >
                  <LayoutDashboard className="mr-2 w-4 h-4" />
                  Dashboard
                </Link>

                <Link
                  to="/profile"
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActivePath('/profile')
                    ? 'bg-dark-700 text-primary-500'
                    : 'text-dark-300 hover:text-dark-100 hover:bg-dark-700'
                    } transition-colors`}
                >
                  <Settings className="mr-2 w-4 h-4" />
                  Settings
                </Link>

                {currentUser.isAdmin && (
                  <Link
                    to="/admin"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActivePath('/admin')
                      ? 'bg-dark-700 text-primary-500'
                      : 'text-dark-300 hover:text-dark-100 hover:bg-dark-700'
                      } transition-colors`}
                  >
                    <Users className="mr-2 w-4 h-4" />
                    Admin
                  </Link>
                )}

                <div className="w-px h-6 bg-dark-700" />

                <div className="px-3 text-sm text-dark-400">
                  {currentUser.email}
                </div>

                <button
                  onClick={logout}
                  className="flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors text-dark-300 hover:text-dark-100 hover:bg-dark-700"
                >
                  <LogOut className="mr-2 w-4 h-4" />
                  Sign Out
                </button>
              </nav>
            </div>
          </div>
        </header>
      )}
      <main className="flex-1">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Layout;